
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import dayjs from "dayjs";
import customerMasterService from "../../../services/masters/customerMaster.Service";
import productMasterService from "../../../services/masters/productMaster.Service";
import uomMasterService from "../../../services/masters/uomMaster.Service";
import customerProductMaster from "../../../services/masters/customerProductMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── CSS Styles (same as PlantAdd) ──────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",

  border: "1px solid #E2E8F0",
  overflow: "hidden",
  mb: 3,
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const readOnlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    background: "#f0f4f8",
  },
};

const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  transition: "all 0.15s ease",
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const CustomerProductAdd = () => {
  const dispatch = useDispatch();
  const { handleSubmit, register, formState: { errors } } = useForm();
  const urlLocation = useLocation();
  const editData = urlLocation.state || null;
  const navigate = useNavigate();
  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  const [customer, setCustomer] = useState([]);
  const [product, setProduct] = useState([]);
  const [uom, setUOM] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [data, setData] = useState({
    cust_id: editData?.cust_id || "",
    cust_name: editData?.cust_name || "",
    prod_id: editData?.prod_id || "",
    prod_code: editData?.prod_code || "",
    prod_description: editData?.prod_description || "",
    rate: editData?.rate || "",
    transport_charges: editData?.transport_charges || "",
    max_holding: editData?.max_holding || "",
    rent_free_days: editData?.rent_free_days || "",
    rental_per_day: editData?.rental_per_day || "",
    uom: editData?.uom || "",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [customerData, productData, uomData] = await Promise.all([
          customerMasterService.getCustomerList({ page: 0, size: 999, searchKey: "" }),
          productMasterService.getProductList({ page: 0, size: 999, searchKey: "" }),
          uomMasterService.getUOMList({ page: 0, size: 999, searchKey: "" }),
        ]);
        setCustomer(customerData);
        setProduct(productData);
        setUOM(uomData);
      } catch (error) {
        console.error("Data fetching failed", error);
        setSuccessMessage("Error fetching dropdown data");
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (editData && product.length > 0) {
      const selectedProduct = product.find((p) => String(p.prod_id) === String(editData.prod_id));
      setData((prev) => ({
        ...prev,
        prod_id: selectedProduct ? selectedProduct.prod_id : "",
        prod_code: selectedProduct ? selectedProduct.prod_code : "",
        prod_description: selectedProduct ? selectedProduct.prod_description : "",
      }));
    }
    if (editData && customer.length > 0) {
      const selectedCustomer = customer.find((c) => String(c.cust_id) === String(editData.cust_id));
      setData((prev) => ({
        ...prev,
        cust_id: selectedCustomer ? selectedCustomer.cust_id : "",
        cust_name: selectedCustomer ? selectedCustomer.cust_name : "",
      }));
    }
  }, [editData, product, customer]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "cust_name" && {
        cust_id: customer.find((c) => c.cust_name === value)?.cust_id || "",
      }),
      ...(name === "prod_code" && {
        prod_id: product.find((p) => p.prod_code === value)?.prod_id || "",
        prod_description: product.find((p) => p.prod_code === value)?.prod_description || "",
      }),
    }));
  };

  const handleBack = () => navigate(-1);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const cpdData = {
      ...data,
      modified_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      modified_by: editData ? userInfo.user_id : null,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      created_by: editData ? editData.created_by : userInfo.user_id,
      changedUpdatedValue: editData ? "edit" : "new",
      cpd_id: editData?.cpd_id || "",
    };
    try {
      if (editData) {
        await customerProductMaster.editDeleteCpd(cpdData);
        setSuccessMessage("Updated successfully");
      } else {
        await customerProductMaster.addCpd(cpdData);
        setSuccessMessage("Added successfully");
      }
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      setSuccessMessage("Error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const customerOptions = customer || [];
  const productOptions = product || [];
  const uomOptions = uom || [];

  return (
    <Box sx={pageSx}>

      {/* Breadcrumb */}
      <Box sx={breadcrumbSx}>
        <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Customer Product Details</span>
      </Box>

      {/* Page Header */}
      <Box sx={pageHeaderSx}>
        <Box sx={pageTitleSx}>
          <Inventory2Icon sx={{ fontSize: 20, color: "#1C3A5E" }} />
          {editData ? "UPDATE" : "ADD"} CUSTOMER PRODUCT DETAILS
        </Box>
        <Button onClick={handleBack} sx={backBtnSx}>
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
        </Button>
      </Box>

      {/* Main Form Card */}
      <Box sx={formCardSx}>

        {/* Navy Strip Header */}
        <Box sx={sectionHeaderSx}>
          <Inventory2Icon sx={{ fontSize: 18, color: "#fff" }} />
          <Typography sx={sectionHeaderTitleSx}>Product Information</Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={formBodySx}>
            <Grid container spacing={3}>

              {/* Customer Name */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Customer Name <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <Autocomplete
                    disablePortal fullWidth
                    options={customerOptions}
                    clearIcon={false}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.cust_name || ""
                    }
                    value={customerOptions.find((c) => String(c.cust_id) === String(data.cust_id)) || null}
                    onChange={(event, newValue) => {
                      setData((prev) => ({
                        ...prev,
                        cust_id: newValue ? newValue.cust_id : "",
                        cust_name: newValue ? newValue.cust_name : "",
                      }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      String(option.cust_id) === String(value.cust_id)
                    }
                    renderOption={(props, option) => (
                      <MenuItem {...props} sx={{ fontSize: "13px" }}>{option.cust_name}</MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params} required fullWidth size="small"
                        placeholder="Select customer"
                        sx={inputSx}
                        {...register("cust_name", { required: "Customer Name is required" })}
                        error={!!errors.cust_name}
                        helperText={errors.cust_name?.message}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Product Code */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Product Code <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <Autocomplete
                    disablePortal fullWidth
                    options={productOptions}
                    clearIcon={false}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.prod_code || ''
                    }
                    value={productOptions.find((p) => String(p.prod_id) === String(data.prod_id)) || null}
                    onChange={(event, newValue) => {
                      setData((prev) => ({
                        ...prev,
                        prod_id: newValue ? newValue.prod_id : "",
                        prod_code: newValue ? newValue.prod_code : "",
                        prod_description: newValue ? newValue.prod_description : "",
                      }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      String(option.prod_id) === String(value.prod_id)
                    }
                    renderOption={(props, option) => (
                      <MenuItem {...props} sx={{ fontSize: "13px" }}>{option.prod_code}</MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params} required fullWidth size="small"
                        placeholder="Select product code"
                        sx={inputSx}
                        {...register("prod_code", { required: "Product Code is required" })}
                        error={!!errors.prod_code}
                        helperText={errors.prod_code?.message}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Product Description (readonly) */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Product Description</Typography>
                  <TextField
                    size="small" fullWidth
                    value={
                      data.prod_description ||
                      (productOptions.find((p) => String(p.prod_id) === String(data.prod_id))?.prod_description ?? "")
                    }
                    sx={readOnlyInputSx}
                    InputProps={{ readOnly: true }}
                    placeholder="Auto-filled from product code"
                  />
                </Box>
              </Grid>

              {/* Rate */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Rate (KG)</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="rate"
                    value={data.rate} sx={inputSx}
                    placeholder="Enter rate per KG"
                  />
                </Box>
              </Grid>

              {/* Transport Charges */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Transport Charges (KG)</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="transport_charges"
                    value={data.transport_charges} sx={inputSx}
                    placeholder="Enter transport charges"
                  />
                </Box>
              </Grid>

              {/* Max Holding */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Max Limit Holding (Days)</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="max_holding"
                    value={data.max_holding} sx={inputSx}
                    placeholder="Enter max holding days"
                  />
                </Box>
              </Grid>

              {/* Rent Free Days */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Rent Free Days</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="rent_free_days"
                    value={data.rent_free_days} sx={inputSx}
                    placeholder="Enter rent free days"
                  />
                </Box>
              </Grid>

              {/* Rent Per Day */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Rent per Day</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="rental_per_day"
                    value={data.rental_per_day} sx={inputSx}
                    placeholder="Enter rent per day"
                  />
                </Box>
              </Grid>

              {/* UOM */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>UOM</Typography>
                  <Autocomplete
                    disablePortal fullWidth
                    options={uomOptions}
                    clearIcon={false}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.uom || ''
                    }
                    value={
                      uomOptions.find((u) => u.uom === data.uom) ||
                      (data.uom ? { uom: data.uom } : null)
                    }
                    onChange={(event, newValue) => {
                      handleChange({
                        target: { name: 'uom', value: newValue ? newValue.uom : '' },
                      });
                    }}
                    isOptionEqualToValue={(option, value) => option.uom === value.uom}
                    renderOption={(props, option) => (
                      <MenuItem {...props} sx={{ fontSize: "13px" }}>{option.uom}</MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params} fullWidth size="small"
                        placeholder="Select UOM" sx={inputSx}
                      />
                    )}
                  />
                </Box>
              </Grid>

            </Grid>
          </Box>

          {/* Form Footer */}
          <Box sx={formFooterSx}>
            <Button sx={cancelBtnSx} onClick={handleBack}>
              Cancel
            </Button>
            <Button type="submit" sx={saveBtnSx} disabled={isSubmitting}>
              <SaveIcon sx={{ fontSize: 18 }} />
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </form>
      </Box>

      <ReusableSnackbar
        open={!!successMessage}
        onClose={(event, reason) => {
          if (reason !== 'clickaway') setSuccessMessage("");
        }}
        message={successMessage}
        autoHideDuration={2000}
        vertical="top"
        horizontal="right"
      />
    </Box>
  );
};

export default CustomerProductAdd;